import React, { useState, useEffect, useCallback, useMemo, useRef } from "react";
import Modal from "@mui/material/Modal";
import {
  Box,
  Button,
  Typography,
  IconButton,
  TextField,
  Tab,
  Tabs,
  CircularProgress,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import toast from "react-hot-toast";
import { resolveProductIntakeApi } from "../../../utils/productIntakeApi";
import { formatToLongDate } from "../../../utils/common";

const MAX_NOTE_LENGTH = 500;

function NoteHistoryItem({ note }) {
  const userName =
    note.created_by_user?.name ||
    [note.created_by_user?.first_name, note.created_by_user?.last_name]
      .filter(Boolean)
      .join(" ") ||
    "Unknown";
  const role = note.created_by_user?.user_type || "";
  const profileImage = note.created_by_user?.profile_image || null;
  const isDecline = note.note_type === "customer_decline";

  return (
    <Box
      sx={{
        border: `1px solid ${isDecline ? "#ffd0d0" : "#e0eaf5"}`,
        borderRadius: "10px",
        p: "14px 16px",
        mb: "10px",
        background: isDecline ? "#fff8f8" : "#f7faff",
      }}
    >
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: "6px" }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: "8px" }}>
          <Box
            sx={{
              width: 34,
              height: 34,
              borderRadius: "50%",
              background: profileImage ? "transparent" : isDecline ? "linear-gradient(135deg, #c0392b 0%, #e74c3c 100%)" : "linear-gradient(135deg, #0B2373 0%, #126ABC 100%)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              color: "#fff",
              fontSize: "13px",
              fontWeight: 700,
              flexShrink: 0,
              overflow: "hidden",
            }}
          >
            {profileImage ? (
              <img
                src={profileImage}
                alt={userName}
                style={{ width: "100%", height: "100%", objectFit: "cover", borderRadius: "50%" }}
                onError={(e) => { e.target.style.display = "none"; e.target.parentNode.innerText = userName.charAt(0).toUpperCase(); }}
              />
            ) : (
              userName.charAt(0).toUpperCase()
            )}
          </Box>
          <Box>
            <Box sx={{ display: "flex", alignItems: "center", gap: "6px" }}>
              <Typography sx={{ fontSize: "13px", fontWeight: 700, color: "#1a2340", lineHeight: 1.2 }}>
                {userName}
              </Typography>
              {isDecline && (
                <Box sx={{ background: "#FF3850", color: "#fff", fontSize: "10px", fontWeight: 700, px: "6px", py: "1px", borderRadius: "20px", lineHeight: 1.6 }}>
                  Decline Note
                </Box>
              )}
            </Box>
            {role && (
              <Typography sx={{ fontSize: "11px", color: "#6b7a99", textTransform: "capitalize" }}>
                {role.replace(/_/g, " ")}
              </Typography>
            )}
          </Box>
        </Box>
        <Typography sx={{ fontSize: "11px", color: "#9C9C9C", flexShrink: 0 }}>
          {formatToLongDate(note.createdAt)}
        </Typography>
      </Box>
      <Typography
        sx={{
          fontSize: "13px",
          color: isDecline ? "#c0392b" : "#333",
          lineHeight: 1.6,
          whiteSpace: "pre-wrap",
          wordBreak: "break-word",
          mt: "4px",
          pl: "42px",
        }}
      >
        {note.note}
      </Typography>
    </Box>
  );
}

export default function AddNotesModal({
  open,
  handleClose,
  row,
  recordList,
  onSaveSuccess,
  intakeScope = "admin",
  defaultTab = 0,
  viewOnly = false,
}) {
  // Memoize so the object reference is stable across renders
  const intakeApi = useMemo(() => resolveProductIntakeApi(intakeScope), [intakeScope]);

  const [tab, setTab] = useState(0);
  const [note, setNote] = useState("");
  const [loading, setLoading] = useState(false);
  const [notes, setNotes] = useState([]);
  const [notesLoading, setNotesLoading] = useState(false);

  // Keep latest values in refs so fetchNotes never needs to be in a dependency array
  const rowIdRef = useRef(row?.id);
  const intakeApiRef = useRef(intakeApi);
  useEffect(() => { rowIdRef.current = row?.id; }, [row?.id]);
  useEffect(() => { intakeApiRef.current = intakeApi; }, [intakeApi]);

  const fetchNotes = useCallback(async () => {
    const id = rowIdRef.current;
    if (!id) return;
    setNotesLoading(true);
    try {
      const res = await intakeApiRef.current.getNotes(id);
      const raw = res?.data?.data;
      setNotes(Array.isArray(raw) ? raw : Array.isArray(raw?.data) ? raw.data : []);
    } catch {
      setNotes([]);
    } finally {
      setNotesLoading(false);
    }
  }, []); // stable — no deps needed because we use refs

  // Only run when modal opens (open flips true)
  useEffect(() => {
    if (!open) return;
    setNote("");
    setTab(viewOnly ? 1 : defaultTab);
    fetchNotes();
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleSubmit = async () => {
    if (!note.trim()) {
      toast.error("Please enter a note.");
      return;
    }
    if (!row?.id) {
      toast.error("Product id is missing.");
      return;
    }
    try {
      setLoading(true);
      const response = await intakeApi.addNote(row.id, { note_to_customer: note.trim() });
      toast.success(response?.data?.message || "Note added successfully!");
      setNote("");
      await fetchNotes();
      setTab(1);
      recordList?.();
      onSaveSuccess?.();
    } catch (error) {
      toast.error(error?.data?.message || "Failed to add note.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal open={open} onClose={loading ? undefined : handleClose}>
      <Box
        sx={{
          position: "absolute",
          top: "50%",
          left: "50%",
          transform: "translate(-50%, -50%)",
          width: 570,
          maxWidth: "calc(100vw - 32px)",
          maxHeight: "85vh",
          background: "#fff",
          borderRadius: "12px",
          outline: "none",
          overflow: "hidden",
          display: "flex",
          flexDirection: "column",
        }}
      >
        {/* Header */}
        <Box
          sx={{
            background: "#126ABC",
            padding: "16px 20px",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <Typography sx={{ color: "#fff", fontWeight: 600, fontSize: "16px" }}>
            Product Notes
          </Typography>
          <IconButton onClick={handleClose} sx={{ color: "#fff", p: 0 }} disabled={loading}>
            <CloseIcon />
          </IconButton>
        </Box>

        {/* Tabs — hidden in viewOnly mode */}
        {!viewOnly && (
          <Box sx={{ borderBottom: "1px solid #e0eaf5", px: "20px", background: "#f7faff" }}>
            <Tabs
              value={tab}
              onChange={(_, v) => setTab(v)}
              sx={{
                minHeight: 40,
                "& .MuiTab-root": { minHeight: 40, fontSize: "13px", fontWeight: 600, textTransform: "none", color: "#6b7a99" },
                "& .Mui-selected": { color: "#126ABC" },
                "& .MuiTabs-indicator": { background: "#126ABC" },
              }}
            >
              <Tab label="Add Note" />
              <Tab label={`History${notes.length ? ` (${notes.length})` : ""}`} />
            </Tabs>
          </Box>
        )}

        {/* Body */}
        <Box sx={{ p: "20px", flex: 1, overflowY: "auto" }}>
          {tab === 0 && (
            <>
              <Typography sx={{ fontWeight: 500, mb: 1, fontSize: "14px", color: "#333" }}>
                Note
              </Typography>
              <TextField
                fullWidth
                multiline
                rows={6}
                placeholder="Eg: The product is not available in Red...."
                value={note}
                onChange={(e) => setNote(e.target.value)}
                disabled={loading}
                inputProps={{ maxLength: MAX_NOTE_LENGTH }}
                error={note.length >= MAX_NOTE_LENGTH}
                helperText={
                  note.length >= MAX_NOTE_LENGTH
                    ? `Maximum ${MAX_NOTE_LENGTH} characters reached.`
                    : `${note.length}/${MAX_NOTE_LENGTH}`
                }
                sx={{
                  "& .MuiOutlinedInput-root": { borderRadius: "8px", fontSize: "14px" },
                  "& .MuiFormHelperText-root": { textAlign: "right" },
                }}
              />
              <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 2 }}>
                <Button
                  variant="contained"
                  onClick={handleSubmit}
                  disabled={loading || !note.trim() || note.length >= MAX_NOTE_LENGTH}
                  sx={{
                    background: "#126ABC",
                    borderRadius: "8px",
                    textTransform: "none",
                    fontWeight: 600,
                    fontSize: "15px",
                    px: 5,
                    height: "46px",
                    "&:hover": { background: "#0f5a9e" },
                    "&.Mui-disabled": { background: "#9BBEE4", color: "#fff" },
                  }}
                >
                  {loading ? <CircularProgress size={20} sx={{ color: "#fff" }} /> : "Submit"}
                </Button>
              </Box>
            </>
          )}

          {tab === 1 && (
            <>
              {notesLoading ? (
                <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
                  <CircularProgress size={32} sx={{ color: "#126ABC" }} />
                </Box>
              ) : notes.length === 0 ? (
                <Box sx={{ textAlign: "center", py: 4 }}>
                  <Typography sx={{ color: "#9C9C9C", fontSize: "14px" }}>
                    No notes added yet.
                  </Typography>
                </Box>
              ) : (
                notes.map((n) => <NoteHistoryItem key={n.id} note={n} />)
              )}
            </>
          )}
        </Box>
      </Box>
    </Modal>
  );
}
